/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import dayjs from 'dayjs'
import {
  ColumnHeightOutlined,
  GiftOutlined,
  UserOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { convertToImperial } from '@sportninja/common/reducers/helpers'
import {
  paragraphSmall,
  paragraphXSmall,
  weightRegular,
} from 'src/components/css'
import { ProfilePlayer } from '../types'

interface Props {
  player: ProfilePlayer | null
  /** Compact variant for the player-switch dropdown (smaller icons + text,
   *  tighter chip gap). Defaults to the full header sizing. */
  compact?: boolean
  /**
   * Override color for both the stat icons and text. Used by the registration
   * card to recolor the stats on selection (dark text on the yellow selected
   * background), mirroring the player name. Omit to keep the default styling.
   */
  color?: string
}

// Player stats chips (age • weight • height). Used by the player header row
// (full size) and the player-switch dropdown (compact). Matches Figma node
// 18025:1268: 4px icon↔text, PRIMARY icons, NEUTRAL_50 text. Each chip is
// hidden when its value is missing.
const ProfilePlayerStatsRow: React.FC<Props> = ({
  player,
  compact = false,
  color,
}) => {
  if (!player) {
    return null
  }

  const birthDate = player.birth_date
  const age = birthDate ? dayjs().diff(birthDate, 'year') : null
  const [ft, inches] = convertToImperial(player.height)

  // When a color override is passed, it wins for both icon and text; otherwise
  // fall back to the variant defaults.
  const colorOverride = color
    ? css`
        color: ${color};
      `
    : null
  const icon = [compact ? compactIconStyle : iconStyle, colorOverride]
  const text = [compact ? compactTextStyle : textStyle, colorOverride]

  return (
    <div css={compact ? compactRowStyle : rowStyle}>
      {age != null && birthDate ? (
        <div css={chipStyle}>
          <GiftOutlined css={icon} />
          <span css={text}>{age} yo</span>
        </div>
      ) : null}

      {player.weight ? (
        <div css={chipStyle}>
          <UserOutlined css={icon} />
          <span css={text}>{player.weight} lbs</span>
        </div>
      ) : null}

      {player.height ? (
        <div css={chipStyle}>
          <ColumnHeightOutlined css={icon} />
          <span css={text}>
            {ft}&apos;{inches}&quot;
          </span>
        </div>
      ) : null}
    </div>
  )
}

export default ProfilePlayerStatsRow

const rowStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
`

const compactRowStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
`

const chipStyle = css`
  display: flex;
  align-items: center;
  gap: 4px;
  /* Keep each stat (icon + value) intact on one line instead of wrapping when
     the row is tight (e.g. inside the fixed-width registration card). */
  flex-shrink: 0;
  white-space: nowrap;
`

const iconStyle = css`
  color: ${colors.PRIMARY};
  font-size: 16px;
`

const compactIconStyle = css`
  color: ${colors.NEUTRAL_50};
  font-size: 12px;
`

const textStyle = css`
  ${paragraphSmall}
  ${weightRegular}
  color: ${colors.NEUTRAL_50};
`

const compactTextStyle = css`
  ${paragraphXSmall}
  ${weightRegular}
  /* Tighter than the preset's 145% — font-size (12px) + 2px. Dropdown only. */
  line-height: 14px;
  color: ${colors.NEUTRAL_50};
`
